// ============================================================
// TATHYA DMS - Dashboard
// Version: 1.0.0
// Date: 2026-04-18
// ============================================================

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDatabases } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const Dashboard = () => {
  const { org_slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDatabases(org_slug)
      .then(res => setDatabases(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [org_slug]);

  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Dashboard</h1>
          <p style={styles.subtitle}>
            Welcome back, {user?.name}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate(`/${org_slug}/databases/new`)}
        >
          + New Database
        </button>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <div className="card" style={styles.statCard}>
          <div style={styles.statNumber}>{databases.length}</div>
          <div style={styles.statLabel}>My Databases</div>
        </div>
        <div className="card" style={styles.statCard}>
          <div style={styles.statNumber}>
            {databases.reduce((sum, db) => sum + (db.DbColumns?.length || 0), 0)}
          </div>
          <div style={styles.statLabel}>Total Columns</div>
        </div>
      </div>

      {/* Recent Databases */}
      <div className="card" style={{ marginTop: '24px' }}>
        <h2 style={styles.sectionTitle}>Recent Databases</h2>
        {loading ? (
          <p style={{ color: '#595959' }}>Loading...</p>
        ) : databases.length === 0 ? (
          <div style={styles.empty}>
            <p>No databases yet.</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/${org_slug}/databases/new`)}
              style={{ marginTop: '12px' }}
            >
              Create your first database
            </button>
          </div>
        ) : (
          <div style={styles.dbGrid}>
            {databases.map(db => (
              <div
                key={db.id}
                style={styles.dbCard}
                onClick={() => navigate(`/${org_slug}/databases/${db.id}`)}
              >
                <div style={styles.dbNumber}>{db.db_number}</div>
                <div style={styles.dbName}>{db.name}</div>
                {db.owner && (
                  <div style={styles.dbOwner}>
                    👤 <span style={styles.dbOwnerName}>{db.owner.name}</span>
                    <span style={styles.dbOwnerEmail}>{db.owner.email}</span>
                  </div>
                )}
                <div style={styles.dbMeta}>
                  {db.DbColumns?.length || 0} columns
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1B3A5C'
  },
  subtitle: {
    color: '#595959',
    fontSize: '14px',
    marginTop: '4px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  },
  statCard: {
    textAlign: 'center',
    padding: '24px'
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#2E75B6'
  },
  statLabel: {
    color: '#595959',
    fontSize: '14px',
    marginTop: '4px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1B3A5C',
    marginBottom: '16px'
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: '#595959'
  },
  dbGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '16px'
  },
  dbCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: 'white'
  },
  dbNumber: {
    fontSize: '11px',
    color: '#2E75B6',
    fontWeight: '600',
    letterSpacing: '0.5px',
    marginBottom: '6px'
  },
  dbName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1B3A5C'
  },
  dbOwner: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexWrap: 'wrap',
    marginTop: '6px',
    fontSize: '11px'
  },
  dbOwnerName: {
    color: '#1B3A5C',
    fontWeight: '600'
  },
  dbOwnerEmail: {
    color: '#888'
  },
  dbMeta: {
    fontSize: '12px',
    color: '#595959',
    marginTop: '6px'
  }
};

export default Dashboard;
